import { type ComponentProps } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon.tsx'

/**
 * Per-size box + glyph pairing on the shared Icon size tokens. The box stays a
 * step larger than the glyph so the spinner has breathing room; `md` keeps the
 * original `size-6` box (no layout shift for StatusButton, whose check/cross
 * companions share that box). `sm`/`lg` step around it.
 */
const spinnerSizes = {
	sm: { box: 'size-4', icon: 'sm' },
	md: { box: 'size-6', icon: 'md' },
	lg: { box: 'size-8', icon: 'lg' },
} as const

export type SpinnerSize = keyof typeof spinnerSizes

/**
 * Spinner — a standalone inline loading indicator: the `update` icon spinning,
 * wrapped in a `role="status"` region so assistive tech announces the busy
 * state. The rotation is dropped under `prefers-reduced-motion`
 * (`motion-reduce:animate-none`).
 *
 * `size` (`sm`/`md`/`lg`, default `md`) scales both the box and the glyph on the
 * shared Icon size tokens — reach for it instead of an ad-hoc `size-*`
 * className. Extracted from StatusButton (which now composes it, passing the
 * inline size) so loading no longer lives only inside the button — it backs any
 * loading/empty state (e.g. the CommandPalette async results). Tokens only.
 */
function Spinner({
	className,
	title = 'loading',
	size = 'md',
	...props
}: ComponentProps<'div'> & { title?: string; size?: SpinnerSize }) {
	const { box, icon } = spinnerSizes[size]
	return (
		<div
			role="status"
			className={cn('inline-flex items-center justify-center', box, className)}
			{...props}
		>
			<Icon
				name="update"
				size={icon}
				className="animate-spin motion-reduce:animate-none"
				title={title}
			/>
		</div>
	)
}

export { Spinner }
